import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  apiErrorMessage,
  useAdminReview,
  useAssignReview,
  useDecideReview,
  type ReviewOutcome,
} from '../lib/api'
import { fmtDateTime, fmtEur } from '../lib/format'

const OUTCOMES: Array<{ key: ReviewOutcome; label: string; cls: string }> = [
  { key: 'approved_funded',     label: 'Approve → funded',     cls: 'btn btn--primary' },
  { key: 'approved_shortlist',  label: 'Approve → shortlist',  cls: 'btn btn--secondary' },
  { key: 'conditional_extra_verif', label: 'Conditional',       cls: 'btn btn--secondary' },
  { key: 'rejected',            label: 'Reject',                cls: 'btn btn--danger' },
  { key: 'banned',              label: 'Ban',                   cls: 'btn btn--danger' },
]

const CARD: React.CSSProperties = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-5)',
}

export function ReviewDetailPage() {
  const { id } = useParams<{ id: string }>()
  const q = useAdminReview(id)
  const assign = useAssignReview()
  const decide = useDecideReview()
  const [notes, setNotes] = useState('')
  const [extraBets, setExtraBets] = useState<number>(20)

  if (!id) return null
  if (q.isLoading) return <div style={{ padding: 'var(--space-4)', color: 'var(--text-secondary)' }}>Loading…</div>
  if (q.isError || !q.data) {
    return <div className="auth-card__error" style={{ display: 'flex' }}>{apiErrorMessage(q.error)}</div>
  }

  const d = q.data
  const decided = !!d.review.decidedAt

  const decideHandler = (outcome: ReviewOutcome) => {
    if (notes.trim().length < 1) {
      alert('Decision notes required.')
      return
    }
    if (outcome === 'conditional_extra_verif') {
      const n = Number(window.prompt('Extra bet count?', String(extraBets)))
      if (!Number.isFinite(n) || n <= 0) return
      setExtraBets(n)
      decide.mutate({ reviewId: id, outcome, decisionNotes: notes, conditionalExtraBetCount: n })
      return
    }
    decide.mutate({ reviewId: id, outcome, decisionNotes: notes })
  }

  return (
    <>
      <header className="page-header">
        <div className="page-header__title-row">
          <div>
            <Link to="/reviews" style={{ fontSize: 'var(--text-sm)', color: 'var(--admin-accent-text)' }}>← Back to queue</Link>
            <h1 className="page-header__title" style={{ marginTop: 'var(--space-1)' }}>Review {d.review.id.slice(0, 8)}…</h1>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0 }}>
              Trader: <span style={{ fontFamily: 'var(--font-mono)' }}>{d.user.email}</span>
            </p>
          </div>
        </div>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-5)', marginBottom: 'var(--space-5)' }}>
        <section style={CARD}>
          <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' }}>Review status</h3>
          <dl style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 'var(--space-2)', columnGap: 'var(--space-3)', fontSize: 'var(--text-sm)', margin: 0 }}>
            <dt style={{ color: 'var(--text-secondary)' }}>Status</dt>
            <dd><span className="admin-pill admin-pill--info">{d.review.status}</span></dd>
            <dt style={{ color: 'var(--text-secondary)' }}>Assigned</dt>
            <dd style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{d.review.assignedReviewerId ?? '—'}</dd>
            <dt style={{ color: 'var(--text-secondary)' }}>Created</dt>
            <dd>{fmtDateTime(d.review.createdAt)}</dd>
            {decided && (<>
              <dt style={{ color: 'var(--text-secondary)' }}>Outcome</dt>
              <dd style={{ fontWeight: 700 }}>{d.review.decisionOutcome}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Decided</dt>
              <dd>{fmtDateTime(d.review.decidedAt)}</dd>
            </>)}
          </dl>
        </section>

        {d.account && (
          <section style={CARD}>
            <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' }}>Account</h3>
            <dl style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 'var(--space-2)', columnGap: 'var(--space-3)', fontSize: 'var(--text-sm)', margin: 0 }}>
              <dt style={{ color: 'var(--text-secondary)' }}>Tier</dt>
              <dd style={{ fontFamily: 'var(--font-mono)' }}>{d.account.tierCode}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Phase</dt>
              <dd>{d.account.currentPhase}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Starting balance</dt>
              <dd>{fmtEur(d.account.startingBalance)}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Current balance</dt>
              <dd>{fmtEur(d.account.currentBalance)}</dd>
            </dl>
          </section>
        )}
      </div>

      {d.latestScore && (
        <section style={{ ...CARD, marginBottom: 'var(--space-5)' }}>
          <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' }}>
            Trader Score · Composite <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--admin-accent-text)' }}>{d.latestScore.composite.toFixed(1)}</span>
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 'var(--space-3)' }}>
            {([['discipline', 'Discipline'], ['edge', 'Edge'], ['consistency', 'Consistency'], ['riskMgmt', 'Risk mgmt'], ['diversity', 'Diversity']] as const).map(([k, label]) => {
              const v = d.latestScore!.components[k]
              return (
                <div key={k} style={{ background: 'var(--bg-subtle)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-3)' }}>
                  <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xl)', fontWeight: 800, marginTop: 'var(--space-1)' }}>{v.toFixed(1)}</div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-5)', marginBottom: 'var(--space-5)' }}>
        <section style={CARD}>
          <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' }}>P1 stats</h3>
          <pre style={{ overflowX: 'auto', background: 'var(--bg-subtle)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-3)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{JSON.stringify(d.p1Stats, null, 2)}</pre>
        </section>
        <section style={CARD}>
          <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' }}>P2 stats</h3>
          <pre style={{ overflowX: 'auto', background: 'var(--bg-subtle)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-3)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{JSON.stringify(d.p2Stats, null, 2)}</pre>
        </section>
      </div>

      {d.timeline.length > 0 && (
        <section style={{ ...CARD, marginBottom: 'var(--space-5)' }}>
          <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' }}>Timeline</h3>
          <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {d.timeline.map((t, idx) => (
              <li key={idx} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', fontSize: 'var(--text-sm)' }}>
                <span style={{ width: 160, flexShrink: 0, fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{fmtDateTime(t.at)}</span>
                <span className="admin-pill admin-pill--info">{t.kind}</span>
                <span>{t.label}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {!decided && (
        <section style={CARD}>
          <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' }}>Decision</h3>

          {!d.review.assignedReviewerId && (
            <button type="button" className="btn btn--secondary btn--sm" disabled={assign.isPending}
              onClick={() => assign.mutate({ reviewId: id })} style={{ marginBottom: 'var(--space-3)' }}>
              {assign.isPending ? 'Assigning…' : 'Assign to me'}
            </button>
          )}

          <div className="field">
            <label className="field__label" htmlFor="dec-notes">Decision notes (required)</label>
            <textarea id="dec-notes" className="field__input" style={{ minHeight: 80 }} value={notes}
              onChange={(e) => setNotes(e.target.value)} placeholder="Reasoning for this decision…" />
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
            {OUTCOMES.map((o) => (
              <button key={o.key} type="button" className={o.cls}
                disabled={decide.isPending || notes.trim().length < 1}
                onClick={() => decideHandler(o.key)}>
                {o.label}
              </button>
            ))}
          </div>
          {decide.isError && (
            <div className="auth-card__error" style={{ display: 'flex', marginTop: 'var(--space-3)' }}>{apiErrorMessage(decide.error)}</div>
          )}
          {decide.isSuccess && (
            <div style={{ color: 'var(--color-success-text)', fontSize: 'var(--text-sm)', marginTop: 'var(--space-3)' }}>Decision saved.</div>
          )}
        </section>
      )}
    </>
  )
}
